import React, { useState } from 'react';
import { Delete, Edit, DragIndicator } from '@mui/icons-material';
import {
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
} from '@mui/material';
import { EventDocumentTemplate } from '../../../../types';
import { eventsService } from '../../../../core/services/eventsService';
import styles from '../SchedulerSetting.module.scss';

interface BanquetEventOrderListProps {
	documents: EventDocumentTemplate[];
	onRefresh: () => void;
	onEditClick: (doc: EventDocumentTemplate) => void;
	canEdit?: boolean;
	canDelete?: boolean;
}

const BanquetEventOrderList: React.FC<BanquetEventOrderListProps> = ({
	documents,
	onRefresh,
	onEditClick,
	canEdit = false,
	canDelete = false,
}) => {
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [docToDelete, setDocToDelete] = useState<EventDocumentTemplate | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);
	const [togglingId, setTogglingId] = useState<number | null>(null);

	const handleDeleteClick = (doc: EventDocumentTemplate) => {
		if (!canDelete) return;
		setDocToDelete(doc);
		setDeleteDialogOpen(true);
	};

	const handleConfirmDelete = async () => {
		if (!docToDelete) return;
		setIsDeleting(true);
		try {
			const ok = await eventsService.deleteEventDocumentTemplate(docToDelete.id);
			if (ok) {
				setDeleteDialogOpen(false);
				setDocToDelete(null);
				onRefresh();
			}
		} catch (e) {
			console.error('Failed to delete banquet event order document:', e);
		} finally {
			setIsDeleting(false);
		}
	};

	const handleCancelDelete = () => {
		setDeleteDialogOpen(false);
		setDocToDelete(null);
	};

	const handleToggleActive = async (doc: EventDocumentTemplate) => {
		if (!canEdit || togglingId !== null) return;
		setTogglingId(doc.id);
		try {
			const ok = await eventsService.toggleEventDocumentTemplateActive(doc);
			if (ok) {
				onRefresh();
			}
		} catch (e) {
			console.error('Failed to toggle banquet event order document:', e);
		} finally {
			setTogglingId(null);
		}
	};

	return (
		<>
			<div className={styles.tableContainer}>
				<table className={styles.workplaceTable}>
					<thead>
						<tr>
							<th style={{ width: 48 }}>Order</th>
							<th>Name</th>
							<th style={{ width: 96, textAlign: 'center' }}>Actions</th>
						</tr>
					</thead>
					<tbody>
						{documents.filter((d) => d.active).length === 0 ? (
							<tr>
								<td colSpan={3} style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
									No records found
								</td>
							</tr>
						) : (
							documents.filter((d) => d.active).map((doc) => (
								<tr key={doc.id}>
									<td style={{ textAlign: 'center', width: 48 }}>
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
											}}
										>
											<DragIndicator sx={{ color: 'text.secondary', mr: 0.5 }} />
											<span>{doc.sortOrder}</span>
										</div>
									</td>
									<td>
										<div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
											<strong>{doc.name}</strong>
											<Chip
												label={doc.active ? 'Active' : 'Inactive'}
												color={doc.active ? 'success' : 'default'}
												size="small"
												variant="outlined"
												onClick={() => canEdit && handleToggleActive(doc)}
												onKeyDown={(e) => {
													if (canEdit && (e.key === 'Enter' || e.key === ' ')) {
														e.preventDefault();
														handleToggleActive(doc);
													}
												}}
												tabIndex={canEdit ? 0 : -1}
												title={
													canEdit
														? 'Click to toggle active / inactive'
														: 'You do not have permission to change status'
												}
												sx={{ cursor: canEdit ? 'pointer' : 'default' }}
											/>
										</div>
									</td>
									<td style={{ textAlign: 'center', width: 96 }}>
										<IconButton
											size="small"
											title={canEdit ? 'Edit name and sort order' : 'You do not have permission to edit'}
											onClick={() => canEdit && onEditClick(doc)}
											className={styles.iconButton}
											disabled={!canEdit}
											style={{ opacity: canEdit ? 1 : 0.5 }}
										>
											<Edit className={styles.editIcon} />
										</IconButton>
										<IconButton
											size="small"
											title={canDelete ? 'Delete' : 'You do not have permission to delete'}
											onClick={() => canDelete && handleDeleteClick(doc)}
											className={styles.iconButton}
											color="error"
											disabled={!canDelete}
											style={{ opacity: canDelete ? 1 : 0.5 }}
										>
											<Delete />
										</IconButton>
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>

			<Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
				<DialogTitle>Delete document</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Are you sure you want to delete &quot;{docToDelete?.name}&quot;? This action cannot be undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelDelete} disabled={isDeleting}>
						Cancel
					</Button>
					<Button onClick={handleConfirmDelete} color="error" disabled={isDeleting}>
						{isDeleting ? 'Deleting...' : 'Delete'}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default BanquetEventOrderList;
